import { Calendar, Clock, ScrollText, X } from 'lucide-react'
import type React from 'react'
import { useEffect, useRef, useState } from 'react'
import styled from 'styled-components'
import type { TablesInsert, TablesUpdate } from '../../../../supabase/functions/_shared/database.types'
import { theme } from '../../theme/theme'
import type { Workout } from '../../types/database.tables'
import { ActionButton } from '../common/ActionButton'
import { Button } from '../common/Button'
import { SportSelector, type SportType } from '../common/SportSelector'

// Extended form data type that includes all ScheduledWorkout fields plus additional full-form fields
interface FullSizeWorkoutFormData extends Partial<Workout> {
  // Additional fields for full form that aren't in ScheduledWorkout
  warmup_instructions: string
  main_set_instructions: string
  cooldown_instructions: string
  equipment_needed: string
  location: string
  notes: string
  target_power: number
  target_heart_rate: string
  rpe_target: number
}

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`

const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.sm};
`

const FormSpacer = styled.div`
  width: 40px;
`

const FormIcon = styled.div`
  padding: ${theme.spacing.sm};
  color: ${({ theme }) => theme.colors.textMuted};
`

const FormFullSize = styled.div`
  flex-grow: 1;
`

const FormSection = styled.div`
  margin-top: ${theme.spacing.md};
  display: flex;
  gap: ${theme.spacing.sm};
`

const FormLabel = styled.label`
  display: block;
  margin-bottom: ${theme.spacing.xs};
  padding: ${theme.spacing.xs} 0;
  color: ${({ theme }) => theme.colors.text};
  font-size: ${theme.typography.md};
  font-weight: 500;
`

const FormSuffix = styled.span`
  font-size: ${theme.typography.sm};
  color: ${({ theme }) => theme.colors.textMuted};
  margin-left: ${theme.spacing.sm};
`

const FormInput = styled.input`
  border: none;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  color: ${({ theme }) => theme.colors.text};
  font-size: ${theme.typography.sm};
  
  background-color: ${({ theme }) => theme.colors.surface};
  border-radius: ${theme.borderRadius.md};
  padding: ${theme.spacing.sm} ${theme.spacing.md};
  
  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
    box-shadow: 0 0 0 2px ${({ theme }) => theme.colors.primary}20;
  }
`

const FormTextarea = styled.textarea`
  width: 100%;
  padding: ${theme.spacing.sm};
  border: 2px solid transparent;
  border-radius: ${theme.borderRadius.sm};
  background-color: ${({ theme }) => theme.colors.surface};
  color: ${({ theme }) => theme.colors.text};
  font-size: ${theme.typography.md};
  min-height: 120px;
  resize: vertical;
  font-family: inherit;
  
  &:focus {
    outline: none;
    border-bottom-color: ${({ theme }) => theme.colors.primary};
    background-color: ${({ theme }) => theme.colors.surfaceHover};
  }
`

const TitleInput = styled.input`
  background-color: transparent;
  border: 0;
  border-bottom: 2px solid ${({ theme }) => theme.colors.border};
  color: ${({ theme }) => theme.colors.text};
  font-size: ${theme.typography.xxl};
  font-weight: 500;
  padding: ${theme.spacing.xs} 0;
  width: 100%;
  margin-bottom: ${theme.spacing.lg};
  margin-left: ${theme.spacing.md};
  margin-right: ${theme.spacing.md};
  
  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
  }
  
  &::placeholder {
    color: ${({ theme }) => theme.colors.textMuted};
    font-weight: 400;
  }
`

type WorkoutCreate = TablesInsert<'workouts'>
type WorkoutUpdate = TablesUpdate<'workouts'>

interface FullSizeWorkoutFormProps {
  onClose: () => void
  onCreate: (workout: WorkoutCreate) => void
  onUpdate: (workout: WorkoutUpdate) => void
  onDelete?: (workoutId: string) => void
  initialData?: Workout
  formData?: Partial<FullSizeWorkoutFormData>
  defaultDate?: string
}

export const WorkoutFullSizeForm: React.FC<FullSizeWorkoutFormProps> = ({
  onClose,
  onCreate,
  onUpdate,
  onDelete,
  initialData,
  formData: initialFormData,
  defaultDate,
}) => {
  const titleInputRef = useRef<HTMLInputElement>(null)

  const getInitialFormData = (): FullSizeWorkoutFormData => ({
    user_id: '',
    scheduled_date: defaultDate || '',
    title: '',
    goal: null,
    time: null,
    duration_minutes: 60,
    instructions: null,
    status: 'scheduled',
    order: 0,
    activity_type: '',
    summary: null,
    // tss: null,
    // discipline: null,
    // template_id: null,
    // intervals: null,
    // Additional fields for full form
    warmup_instructions: '',
    main_set_instructions: '',
    cooldown_instructions: '',
    equipment_needed: '',
    location: '',
    notes: '',
    target_power: 0,
    target_heart_rate: '',
    rpe_target: 0,
    ...(initialFormData || {}),
  })

  const [formData, setFormData] = useState<FullSizeWorkoutFormData>(getInitialFormData())

  useEffect(() => {
    if (initialData) {
      setFormData((prev) => ({
        ...prev,
        ...initialData,
        goal: initialData.goal ?? '',
        instructions: initialData.instructions ?? '',
        summary: initialData.summary ?? '',
        time: initialData.time ?? '',
        status: initialData.status ?? 'scheduled',
        // tss: initialData.tss ?? 0,
        // discipline: initialData.discipline,
        // template_id: initialData.template_id ?? undefined,
        // intervals: initialData.intervals ?? undefined,
        // Extended fields
        warmup_instructions: '',
        main_set_instructions: '',
        cooldown_instructions: '',
        equipment_needed: '',
        location: '',
        notes: '',
        target_power: 0,
        target_heart_rate: '',
        rpe_target: 0,
      }))
    } else if (initialFormData) {
      setFormData((prev) => ({
        ...prev,
        ...initialFormData,
      }))
    }
  }, [initialData, initialFormData])

  // Handle escape key to close modal
  useEffect(() => {
    const handleEscapeKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose()
      }
    }

    document.addEventListener('keydown', handleEscapeKey)
    return () => {
      document.removeEventListener('keydown', handleEscapeKey)
    }
  }, [onClose])

  useEffect(() => {
    if (titleInputRef.current) {
      setTimeout(() => {
        titleInputRef.current?.focus()
      }, 100)
    }
  }, [])

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (formData.id) {
      handleCreate()
    } else {
      handleUpdate()
    }
    onClose()
  }

  const handleCreate = () => {
    onCreate({
      user_id: formData.user_id ?? '',
      scheduled_date: formData.scheduled_date ?? '',
      title: formData.title ?? '',
      goal: formData.goal || null,
      duration_minutes: formData.duration_minutes ?? 0,
      instructions: formData.instructions || null,
      status: formData.status || 'scheduled',
      order: formData.order || 0,
      activity_type: formData.activity_type || 'unknown',
      summary: formData.summary || null,
      time: formData.time ?? null,
    })
    onClose()
  }

  const handleUpdate = () => {
    onUpdate({
      user_id: formData.user_id ?? '',
      scheduled_date: formData.scheduled_date ?? '',
      title: formData.title ?? '',
      goal: formData.goal || null,
      duration_minutes: formData.duration_minutes ?? 0,
      instructions: formData.instructions || null,
      status: formData.status || 'scheduled',
      order: formData.order || 0,
      activity_type: formData.activity_type || 'unknown',
      summary: formData.summary || null,
      time: formData.time ?? null,
    })
    onClose()
  }

  const handleDelete = () => {
    if (initialData && onDelete) {
      onDelete(initialData.id)
      onClose()
    }
  }

  return (
    <form onSubmit={handleSubmit}>
      <Header>
        <ActionButton onClick={onClose} title="Close">
          <X size={36} />
        </ActionButton>

        <TitleInput
          ref={titleInputRef}
          type="text"
          value={formData.title}
          onChange={(e) => setFormData((prev) => ({ ...prev, title: e.target.value }))}
          placeholder="Enter workout title..."
        />

        <HeaderActions>
          <Button variant="primary" onClick={handleSubmit}>
            Save
          </Button>

          {initialData && onDelete && (
            <Button variant="secondary" onClick={handleDelete}>
              Delete
            </Button>
          )}
        </HeaderActions>
      </Header>

      <FormSection>
        <FormIcon>
          <Calendar size={24} />
        </FormIcon>

        <FormInput
          type="date"
          value={formData.scheduled_date}
          onChange={(e) => setFormData((prev) => ({ ...prev, scheduled_date: e.target.value }))}
          required
        />

        <FormInput
          type="time"
          value={formData.time || ''}
          onChange={(e) => setFormData((prev) => ({ ...prev, time: e.target.value }))}
        />

        <Button disabled>Time zone</Button>
      </FormSection>

      <FormSection>
        <FormSpacer></FormSpacer>
        <SportSelector
          value={formData.activity_type as SportType}
          onChange={(sport) => setFormData((prev) => ({ ...prev, activity_type: sport }))}
          label=""
        />
      </FormSection>

      <FormSection>
        <FormIcon>
          <Clock size={24} />
        </FormIcon>

        <FormFullSize>
          <FormLabel>Duration</FormLabel>
          <FormInput
            type="number"
            value={formData.duration_minutes || ''}
            onChange={(e) => setFormData((prev) => ({ ...prev, duration_minutes: parseInt(e.target.value) || 0 }))}
            min="1"
            max="10000"
            required
          />
          <FormSuffix>Minutes</FormSuffix>
        </FormFullSize>
      </FormSection>

      {/*<FormSection>
          <FormSpacer></FormSpacer>
          <FormTabs>
            <TabTitle $active={selectedTab === 'instructions'}
                      onClick={(e) => {
                        setSelectedTab('instructions');
                        e.preventDefault();
                        e.stopPropagation();
                      }}>Instructions</TabTitle>
            <TabTitle $active={selectedTab === 'structure'}
                      onClick={(e) => {
                        setSelectedTab('structure');
                        e.preventDefault();
                        e.stopPropagation();
                      }}>Structure</TabTitle>
          </FormTabs>
        </FormSection>*/}

      <FormSection>
        <FormIcon>
          <ScrollText size={24} />
        </FormIcon>
        <FormFullSize>
          <FormLabel>Instructions</FormLabel>
          <FormTextarea
            value={formData.instructions || ''}
            onChange={(e) => setFormData((prev) => ({ ...prev, instructions: e.target.value || null }))}
            placeholder="Additional notes, weather considerations, technique focus..."
          />
        </FormFullSize>
      </FormSection>

      {/*<FormRowContainer>
          <FormRow>
            <FormIcon>
              <LayoutGrid size={24} />
            </FormIcon>
            <FormFullSize>
              <FormLabel>Structure</FormLabel>
              <FormTextarea
                  value={formData.summary || ''}
                  onChange={(e) => setFormData(prev => ({ ...prev, summary: e.target.value || null }))}
                  placeholder="e.g. 15min warmup, 10min main set as 5x (1min Threshold / 1min Rest), 10min cooldown"
              />
            </FormFullSize>
          </FormRow>
        </FormRowContainer>*/}
    </form>
  )
}
